import type { Candle } from '@termix/shared';

export type LinePoint = { time: number; value: number };

export const smaSeries = (candles: Candle[], length: number): LinePoint[] => {
  const out: LinePoint[] = [];
  let sum = 0;
  for (let i = 0; i < candles.length; i++) {
    sum += candles[i].close;
    if (i >= length) sum -= candles[i - length].close;
    if (i >= length - 1) out.push({ time: candles[i].time, value: sum / length });
  }
  return out;
};

export const emaSeries = (candles: Candle[], length: number): LinePoint[] => {
  if (!candles.length) return [];
  const k = 2 / (length + 1);
  const out: LinePoint[] = [];
  let ema = candles[0].close;
  for (let i = 0; i < candles.length; i++) {
    ema = i === 0 ? candles[0].close : candles[i].close * k + ema * (1 - k);
    if (i >= length - 1) out.push({ time: candles[i].time, value: ema });
  }
  return out;
};

export const rsiSeries = (candles: Candle[], length = 14): LinePoint[] => {
  const out: LinePoint[] = [];
  if (candles.length <= length) return out;
  let gains = 0;
  let losses = 0;
  for (let i = 1; i <= length; i++) {
    const d = candles[i].close - candles[i - 1].close;
    if (d >= 0) gains += d; else losses -= d;
  }
  let avgGain = gains / length;
  let avgLoss = losses / length;
  const push = (i: number) => {
    const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    const value = avgLoss === 0 ? 100 : 100 - 100 / (1 + rs);
    out.push({ time: candles[i].time, value });
  };
  push(length);
  for (let i = length + 1; i < candles.length; i++) {
    const d = candles[i].close - candles[i - 1].close;
    const gain = d > 0 ? d : 0;
    const loss = d < 0 ? -d : 0;
    avgGain = (avgGain * (length - 1) + gain) / length;
    avgLoss = (avgLoss * (length - 1) + loss) / length;
    push(i);
  }
  return out;
};

export const volumePoints = (candles: Candle[]): Array<{ time: number; value: number; color: string }> =>
  candles.map((c) => ({
    time: c.time,
    value: Number(c.volume) || 0,
    color: c.close >= c.open ? 'rgba(61,186,122,0.45)' : 'rgba(226,91,91,0.45)',
  }));

export type SwingPoint = { index: number; time: number; price: number; kind: 'high' | 'low' };

/** Williams-style fractal swings (wing = bars each side). */
export const findSwings = (candles: Candle[], wing = 2): SwingPoint[] => {
  const w = Math.max(1, Math.min(5, Math.floor(wing) || 2));
  const out: SwingPoint[] = [];
  for (let i = w; i < candles.length - w; i++) {
    let isHigh = true;
    let isLow = true;
    const h = candles[i].high;
    const l = candles[i].low;
    for (let j = 1; j <= w; j++) {
      if (candles[i - j].high >= h || candles[i + j].high > h) isHigh = false;
      if (candles[i - j].low <= l || candles[i + j].low < l) isLow = false;
    }
    if (isHigh) out.push({ index: i, time: candles[i].time, price: h, kind: 'high' });
    else if (isLow) out.push({ index: i, time: candles[i].time, price: l, kind: 'low' });
  }
  return out;
};

export type AutoLevel = { price: number; touches: number; kind: 'high' | 'low' | 'mixed' };

/**
 * Cluster swing prices into horizontal hint levels.
 * `sensitivity` 0–100: lower = stricter wings / tighter clusters / fewer levels.
 */
export const autoLevels = (
  candles: Candle[],
  count = 6,
  wing = 2,
  sensitivity = 50,
): AutoLevel[] => {
  const sens = Math.max(0, Math.min(100, Number(sensitivity) || 50)) / 100;
  const w = Math.max(1, Math.min(5, Math.round(wing + (1 - sens) * 2)));
  if (candles.length < w * 2 + 5) return [];
  const swings = findSwings(candles, w);
  if (!swings.length) return [];
  const mid = candles.reduce((s, c) => s + c.close, 0) / candles.length;
  // Strict → 0.12% cluster; loose → 0.45%
  const tol = mid * (0.0012 + sens * 0.0033);
  const minTouches = sens < 0.35 ? 2 : 1;
  const clusters: Array<{ price: number; touches: number; highs: number; lows: number }> = [];
  for (const s of swings) {
    let hit = clusters.find((c) => Math.abs(c.price - s.price) <= tol);
    if (!hit) {
      hit = { price: s.price, touches: 0, highs: 0, lows: 0 };
      clusters.push(hit);
    }
    hit.touches += 1;
    hit.price = (hit.price * (hit.touches - 1) + s.price) / hit.touches;
    if (s.kind === 'high') hit.highs += 1;
    else hit.lows += 1;
  }
  const want = Math.max(1, Math.min(12, Math.floor(count) || 6));
  return clusters
    .filter((c) => c.touches >= minTouches)
    .sort((a, b) => b.touches - a.touches || Math.abs(a.price - mid) - Math.abs(b.price - mid))
    .slice(0, want)
    .map((c) => ({
      price: c.price,
      touches: c.touches,
      kind: c.highs && c.lows ? 'mixed' as const : c.highs ? 'high' as const : 'low' as const,
    }))
    .sort((a, b) => b.price - a.price);
};

export type AutoTrend = {
  t1: number; p1: number; t2: number; p2: number; kind: 'up' | 'down';
};

/**
 * Connect recent swing lows (up) / highs (down) as visual trend hints.
 * Segments are extended past both pivots so the ray is obvious on the chart.
 */
export const autoTrends = (
  candles: Candle[],
  count = 2,
  wing = 2,
  sensitivity = 50,
): AutoTrend[] => {
  if (candles.length < 30) return [];
  const sens = Math.max(0, Math.min(100, Number(sensitivity) || 50)) / 100;
  const w = Math.max(1, Math.min(5, Math.round(wing + (1 - sens) * 2)));
  const swings = findSwings(candles, w);
  const lows = swings.filter((s) => s.kind === 'low');
  const highs = swings.filter((s) => s.kind === 'high');
  const out: AutoTrend[] = [];
  const pairs = Math.max(1, Math.min(4, Math.floor(count) || 2));
  const upSlop = 1 - (0.0005 + (1 - sens) * 0.002);
  const dnSlop = 1 + (0.0005 + (1 - sens) * 0.002);
  const lastT = Number(candles[candles.length - 1].time);
  const firstT = Number(candles[0].time);
  const span = Math.max(1, lastT - firstT);

  const pushFrom = (pts: SwingPoint[], kind: 'up' | 'down') => {
    if (pts.length < 2) return;
    const look = Math.max(5, pairs * 3 + Math.round(sens * 5));
    const recent = pts.slice(-look);
    for (let i = recent.length - 1; i >= 1 && out.filter((t) => t.kind === kind).length < pairs; i--) {
      const a = recent[i - 1];
      const b = recent[i];
      if (a.time === b.time) continue;
      if (kind === 'up' && b.price < a.price * upSlop) continue;
      if (kind === 'down' && b.price > a.price * dnSlop) continue;
      const dt = b.time - a.time;
      const dp = b.price - a.price;
      if (!(dt > 0)) continue;
      if (dt < span * 0.01 && sens < 0.7) continue;
      // Extend past both pivots so the ray is obvious (slightly beyond the frame).
      const back = 0.45;
      const fwd = Math.max(1.0, (lastT - b.time) / dt + 0.55);
      out.push({
        kind,
        t1: Math.max(firstT, a.time - dt * back),
        p1: a.price - dp * back,
        t2: Math.min(lastT + Math.max(dt, 60), b.time + dt * fwd),
        p2: b.price + dp * fwd,
      });
    }
  };
  pushFrom(lows, 'up');
  pushFrom(highs, 'down');
  return out.slice(0, pairs * 2);
};

export {
  orderBlockHints,
  orderBlockBreakers,
  enrichOrderBlocksWithBook,
  type OrderBlockStatus,
  type OrderBlockHint,
  type BookDepth,
  type OrderBlockBookMode,
} from '@termix/shared';

/** Merge candle arrays by time; keep the newest maxBars. */
export const mergeCandleSeries = (
  a: Candle[],
  b: Candle[],
  maxBars: number,
): Candle[] => {
  const by = new Map<number, Candle>();
  for (const c of a) {
    const t = Number(c.time);
    if (Number.isFinite(t)) by.set(t, c);
  }
  for (const c of b) {
    const t = Number(c.time);
    if (Number.isFinite(t)) by.set(t, c);
  }
  const sorted = [...by.values()].sort((x, y) => x.time - y.time);
  const cap = Math.max(50, Math.floor(maxBars) || 1000);
  return sorted.length > cap ? sorted.slice(-cap) : sorted;
};
